import { Component } from 'react';
import { v4 } from 'uuid';
import { ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { chartHelper } from 'helpers';
import { withChartLogic } from 'components/common';
import { TRANSFUSION_DETAILS_FIELD_CONFIGS, TransfusionDetailsFormField, VITAL_SIGN_CONFIGS, cloneFormFieldMap } from './constants';
import TransfusionDetailsSidebarView from './TransfusionDetailsSidebarView';

export interface TransfusionVitalSignRow {
  vitalSign: string;
  beforeTransfusion: string;
  fifteenMinutesAfter: string;
  immediatelyAfter: string;
}

export interface TransfusionDetailEntry {
  id: string;
  active: boolean;
  day: string;
  timeBegun: string;
  timeCompleted: string;
  vitalSigns: TransfusionVitalSignRow[];
}

export interface TransfusionDetailsSidebarProps extends ChartComponentProps {
  isSidebarOpen: boolean;
  hasSavedEntry: boolean;
  resetSignal: number;
  addTransfusionDetail: (entry: TransfusionDetailEntry | null) => void;
  handleCloseSidebar: Function;
}

const getFieldValue = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: string): string => {
  const formField = formFieldMap?.get(fieldName);
  if (!formField) return '';

  const selectedContent = formField.chartContent?.find((item) => !!item.value && item.selected);
  return String(selectedContent?.label ?? formField.value ?? '').trim();
};

export class TransfusionDetailsSidebar extends Component<TransfusionDetailsSidebarProps> {
  static displayName = 'TransfusionDetailsSidebar';

  // The values captured at the last Save. The step keeps only derived rows, which cannot be mapped
  // back onto fields, so this snapshot is what an abandoned edit reverts to.
  lastSavedFormFields: Map<string, ChartMetaFormField> | null = null;

  componentDidMount() {
    this.props.initState(this.buildFormFields());
  }

  componentDidUpdate(prevProps: TransfusionDetailsSidebarProps) {
    if (prevProps.resetSignal !== this.props.resetSignal) {
      // A step reset clears the saved rows too, so the old snapshot must not survive it.
      this.lastSavedFormFields = null;
      this.props.initState(this.buildFormFields());
    }
  }

  // Closing is the only exit that is not a Save, so it always drops unsaved edits: back to the last
  // saved values, or to blank fields when nothing has been saved yet.
  handleCloseClick = (...args: unknown[]) => {
    this.props.initState(this.lastSavedFormFields ? cloneFormFieldMap(this.lastSavedFormFields) : this.buildFormFields());
    this.props.handleCloseSidebar(...args);
  };

  buildFormFields = () => {
    const dataMap = new Map<string, ChartMetaFormField>();

    TRANSFUSION_DETAILS_FIELD_CONFIGS.forEach(({ name, label, type, isRequired }) => {
      dataMap.set(name, { ...chartHelper.createFormField({ name, label, type }), isRequired: Boolean(isRequired) });
    });

    return dataMap;
  };

  buildEntry = (): TransfusionDetailEntry => {
    const { formFieldMap } = this.props;

    return {
      id: v4(),
      active: true,
      day: getFieldValue(formFieldMap, TransfusionDetailsFormField.DAY),
      timeBegun: getFieldValue(formFieldMap, TransfusionDetailsFormField.TIME_BEGUN),
      timeCompleted: getFieldValue(formFieldMap, TransfusionDetailsFormField.TIME_COMPLETED),
      vitalSigns: VITAL_SIGN_CONFIGS.map((vitalSign) => ({
        vitalSign: vitalSign.label,
        beforeTransfusion: getFieldValue(formFieldMap, vitalSign.beforeField),
        fifteenMinutesAfter: getFieldValue(formFieldMap, vitalSign.fifteenMinutesField),
        immediatelyAfter: getFieldValue(formFieldMap, vitalSign.immediatelyAfterField)
      }))
    };
  };

  isEntryComplete = (): boolean =>
    TRANSFUSION_DETAILS_FIELD_CONFIGS.filter((fieldConfig) => fieldConfig.isRequired).every((fieldConfig) => Boolean(getFieldValue(this.props.formFieldMap, fieldConfig.name)));

  isEntryEmpty = (): boolean => TRANSFUSION_DETAILS_FIELD_CONFIGS.every((fieldConfig) => !getFieldValue(this.props.formFieldMap, fieldConfig.name));

  // Values are kept after save so the entry can be edited via the edit icon on the step, and the
  // saved snapshot becomes the baseline an abandoned edit reverts to.
  // Saving an empty entry (after a reset) clears the saved entry on the step.
  handleSaveClick = () => {
    this.props.addTransfusionDetail(this.isEntryEmpty() ? null : this.buildEntry());
    this.lastSavedFormFields = cloneFormFieldMap(this.props.formFieldMap);
  };

  render() {
    const chartActionsProps = {
      // Saving with no values is allowed once an entry exists so a reset drawer can clear it.
      enableSaveButton: this.isEntryComplete() || (this.props.hasSavedEntry && this.isEntryEmpty()),
      saveButtonText: 'Save',
      onSaveClick: this.handleSaveClick
    };
    const viewProps = {
      chartActionsProps,
      formFieldMap: this.props.formFieldMap,
      formSubmittedCount: this.props.formSubmittedCount,
      isSidebarOpen: this.props.isSidebarOpen,
      onCloseClick: this.handleCloseClick,
      sidebarProps: this.props.sidebarProps
    };
    return <TransfusionDetailsSidebarView {...viewProps} />;
  }
}

export { TransfusionDetailsSidebar as BaseTransfusionDetailsSidebar };
export default withChartLogic(TransfusionDetailsSidebar);
